/**
 * use-auth — 登录 / 2FA / refresh / logout API hooks
 *
 * 后端契约参考：dev-docs 30-鉴权与会话 + P0-admin-auth-rbac
 */

import type { AdminUserResponse, InviteRequest, PendingInviteResponse } from '@novel-admin/shared';
import { apiRequest } from '~/api/client';
import { queryClient } from '~/lib/query-client';
import { useAuthStore } from '~/stores/auth-store';

// 「记住设备 30 天」可信设备 token 持久化（localStorage，仅本设备）；
// 下次登录带上 → 服务端校验有效则跳过 TOTP 第二步
const DEVICE_TOKEN_KEY = 'na-device-token';
function getDeviceToken(): string | undefined {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
function setDeviceToken(token: string | null | undefined): void {
  try {
    if (token) localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    // localStorage 不可用（隐私模式）→ 静默降级，下次仍走完整 2FA
  }
}

type LoginStageVerify2fa = {
  stage: 'verify_2fa_required';
  verify_2fa_token: string;
  expires_in_sec: number;
};

type LoginStageBindTotp = {
  stage: 'bind_totp_required';
  bind_totp_token: string;
  expires_in_sec: number;
};

type LoginStageAuthenticated = {
  stage: 'authenticated';
  access: string;
  refresh: string;
  user: AdminUserResponse;
  expires_in_sec: number;
  /** 「记住设备」勾选时服务端返回的可信设备 token（verify-2fa 路径）*/
  device_token?: string | null;
};

export type LoginResponse = LoginStageVerify2fa | LoginStageBindTotp | LoginStageAuthenticated;

export async function login(email: string, password: string): Promise<LoginResponse> {
  return apiRequest<LoginResponse>({
    method: 'POST',
    path: '/api/v1/auth/login',
    // 本设备若存过「记住设备」token 则带上 → 服务端校验有效则跳过 TOTP
    body: { email, password, device_token: getDeviceToken() },
  });
}

export async function verify2FA(
  verify2faToken: string,
  payload: { code?: string; backup_code?: string },
  opts?: { remember?: boolean; trustDevice?: boolean },
): Promise<LoginStageAuthenticated> {
  const res = await apiRequest<LoginStageAuthenticated>({
    method: 'POST',
    path: '/api/v1/auth/verify-2fa',
    body: { ...payload, remember: opts?.remember, trust_device: opts?.trustDevice },
    bearerOverride: verify2faToken,
  });
  // 勾选「记住设备」时服务端返回 device_token，持久化供下次登录跳过 TOTP
  if (res.device_token) setDeviceToken(res.device_token);
  return res;
}

// refresh / 会话续期已统一到 ~/api/session-refresh 的 refreshAccessToken（含 in-flight 去重 +
// access 过期 401→隐式续期重试 单点）。此处不再保留独立 refreshSession——两份 in-flight 单例并存
// 会让并发刷新互相消费对方轮换的 token 反吊销（根因报告 2026-06-23「登录约 15min 自动登出」）。

export async function logout(allDevices = false): Promise<void> {
  try {
    await apiRequest({
      method: 'POST',
      path: '/api/v1/auth/logout',
      body: { all_devices: allDevices },
    });
  } finally {
    useAuthStore.getState().clearSession();
    // 退出登录统一清业务缓存（通用安全兜底）：同浏览器换账号时若不清，上一个账户的
    // 列表 / 会话 / 设备等 query data 会泄漏给下一个账户（部分 query key 不带 userId）。
    queryClient.clear();
  }
}

export async function getMe(): Promise<AdminUserResponse> {
  return apiRequest<AdminUserResponse>({ method: 'GET', path: '/api/v1/auth/me' });
}

/**
 * 邀请运营成员（POST /api/v1/auth/invite）。
 * 契约 InviteRequestSchema={ email, nickname, role }（role 为 4 角色闭集）；
 * 后端 requireRole('root','admin')，且仅 root 可邀请 root；成功落 admin_audit_logs。
 * 返回服务端创建的邀请 id（邀请落 admin_invites，待接受出现在 GET /auth/invites；
 * 接受后才建 admin_users 行——故团队成员列表不含 pending 行）。
 */
export async function inviteMember(payload: InviteRequest): Promise<{ invite_id: string }> {
  return apiRequest({ method: 'POST', path: '/api/v1/auth/invite', body: payload });
}

/** GET /api/v1/auth/invites — 待接受邀请（未消费，含过期未接受）；root/admin。 */
export async function listPendingInvites(): Promise<PendingInviteResponse[]> {
  return apiRequest<PendingInviteResponse[]>({ method: 'GET', path: '/api/v1/auth/invites' });
}

/** POST /api/v1/auth/invites/:id/resend — 重发邀请（轮转 token + 刷新 24h + 重发邮件）；root/admin。 */
export async function resendInvite(
  id: string,
): Promise<{ id: string; email: string; expires_at: string }> {
  return apiRequest({ method: 'POST', path: `/api/v1/auth/invites/${id}/resend` });
}

export async function acceptInvite(payload: {
  invite_token: string;
  password: string;
  nickname: string;
}): Promise<{
  user: AdminUserResponse;
  totp_setup: { secret_base32: string; otpauth_uri: string };
}> {
  return apiRequest({
    method: 'POST',
    path: '/api/v1/auth/accept-invite',
    body: payload,
  });
}

/**
 * 用 bind-totp-required 临时 token 重新拉 TOTP 绑定 challenge（codex round-3 #12）
 * 让用户在关掉 AcceptInvitePage / 丢失 OTP URI 后仍能完成绑定
 */
export async function getTotpSetup(
  bindTotpToken: string,
): Promise<{ secret_base32: string; otpauth_uri: string }> {
  return apiRequest({
    method: 'GET',
    path: '/api/v1/auth/totp-setup',
    bearerOverride: bindTotpToken,
  });
}

/**
 * 完成 TOTP 首次绑定（codex P1 #2 后）：
 * - 接受 bind-totp-required 临时 token 作为 bearer（来自 login bind_totp_required stage）
 * - 服务端绑定成功后同事务 issue 完整 access+refresh；客户端一步转入登录态
 */
export async function bindTotp(
  bindTotpToken: string,
  code: string,
  opts?: { remember?: boolean },
): Promise<{
  backup_codes: string[];
  access: string;
  refresh: string;
  user: AdminUserResponse;
  expires_in_sec: number;
}> {
  return apiRequest({
    method: 'POST',
    path: '/api/v1/auth/bind-totp',
    body: { code, remember: opts?.remember },
    bearerOverride: bindTotpToken,
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiRequest({
    method: 'POST',
    path: '/api/v1/auth/password-reset/request',
    body: { email },
  });
}

export async function confirmPasswordReset(resetToken: string, password: string): Promise<void> {
  await apiRequest({
    method: 'POST',
    path: '/api/v1/auth/password-reset/confirm',
    body: { reset_token: resetToken, password },
  });
}
